import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import { CartProvider } from "./context/CartContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { WishlistProvider } from "./context/WishlistContext";
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import { NewsletterPopup } from "./components/NewsletterPopup";
import { LiveChatWidget } from "./components/LiveChatWidget";

import { HomePage } from "./pages/HomePage";
import { ShopPage } from "./pages/ShopPage";
import { ProductPage } from "./pages/ProductPage";
import { CartPage } from "./pages/CartPage";
import { CheckoutSuccessPage } from "./pages/CheckoutSuccessPage";
import { ReviewsPage } from "./pages/ReviewsPage";
import { FAQPage } from "./pages/FAQPage";
import { AboutPage } from "./pages/AboutPage";
import { ContactPage } from "./pages/ContactPage";
import { AdminPage } from "./pages/AdminPage";
import { PolicyPage } from "./pages/PolicyPage";
import { StacksListPage, StackDetailPage } from "./pages/StacksPage";
import { StackBuilderPage } from "./pages/StackBuilderPage";
import { LabStandardsPage } from "./pages/LabStandardsPage";
import { OrderInstructionsPage } from "./pages/OrderInstructionsPage";
import { LoginPage } from "./pages/LoginPage";
import { RegisterPage } from "./pages/RegisterPage";
import { AccountPage } from "./pages/AccountPage";
import NotFoundPage from "./pages/NotFoundPage";
import OfficialSitePage from "./pages/OfficialSitePage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import { useAnalyticsAndVerification } from "./lib/analytics";
import { AuthCallback } from "./pages/AuthCallback";
import { TrackOrderPage } from "./pages/TrackOrderPage";
import { WishlistPage } from "./pages/WishlistPage";
import { CoaLibraryPage } from "./pages/CoaLibraryPage";
import { initTracker, trackPageView } from "./lib/tracker";
import { MaintenancePage } from "./pages/MaintenancePage";
import { ComplianceBanner } from "./components/ComplianceBanner";
import api from "./lib/api";

const ScrollToTop = () => {
    const { pathname } = useLocation();
    React.useEffect(() => {
        window.scrollTo({ top: 0, behavior: "instant" });
    }, [pathname]);
    return null;
};

const TrackerBootstrap = () => {
    const { pathname, search } = useLocation();
    React.useEffect(() => {
        initTracker({ ga4MeasurementId: process.env.REACT_APP_GA4_MEASUREMENT_ID });
    }, []);
    React.useEffect(() => {
        if (pathname.startsWith("/admin")) return;
        trackPageView();
    }, [pathname, search]);
    return null;
};

const AnalyticsBootstrap = () => {
    useAnalyticsAndVerification();
    return null;
};

/**
 * MaintenanceGate — polls /api/site/maintenance and, when enabled, replaces
 * the entire storefront with the maintenance page. Admins bypass.
 */
const MaintenanceGate = ({ children }) => {
    const location = useLocation();
    const [status, setStatus] = React.useState(null);

    const refresh = React.useCallback(async () => {
        try {
            const { data } = await api.get("/site/maintenance");
            setStatus(data);
        } catch {
            setStatus({ enabled: false });
        }
    }, []);

    React.useEffect(() => {
        refresh();
        const t = setInterval(refresh, 30000);
        return () => clearInterval(t);
    }, [refresh]);

    if (status === null) return null;

    const isAdminRoute = location.pathname.startsWith("/admin");
    const hasAdminToken = !!(typeof window !== "undefined" && localStorage.getItem("admin_token"));
    const previewFlag =
        new URLSearchParams(location.search).get("preview") === "maintenance";

    if (previewFlag && hasAdminToken) {
        return (
            <MaintenancePage
                primary={status.primary_message}
                secondary={status.secondary_message}
                preview
            />
        );
    }

    if (status.enabled && !isAdminRoute && !hasAdminToken) {
        return (
            <MaintenancePage
                primary={status.primary_message}
                secondary={status.secondary_message}
            />
        );
    }

    return children;
};

// ---------------------------------------------------------------------------
// Routing architecture — the two shells.
//
// The storefront shell (Header + ComplianceBanner + Footer + Newsletter + Chat)
// ONLY renders for authenticated customers, admin routes and the public
// /official microsite. Unauthenticated visitors see ONLY the standalone
// gateway page — never a flash of header/footer/newsletter/products.
// ---------------------------------------------------------------------------

const AUTH_ONLY_PATHS = new Set([
    "/login",
    "/register",
    "/forgot-password",
    "/reset-password",
]);

const BYPASS_GATE_PREFIXES = ["/admin", "/official", "/auth/callback"];

const isAuthOnlyPath = (pathname) => AUTH_ONLY_PATHS.has(pathname);
const isBypassPath = (pathname) =>
    BYPASS_GATE_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/"));

const StandaloneGatewayRoutes = () => (
    <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        {/* Any other direct hit in standalone mode routes to login so the
         *  Navigate below carries the next= correctly. */}
        <Route path="*" element={<LoginPage />} />
    </Routes>
);

const StorefrontRoutes = () => {
    const location = useLocation();
    if (location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }
    return (
        <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/shop" element={<ShopPage />} />
            <Route path="/product/:slug" element={<ProductPage />} />
            <Route path="/cart" element={<CartPage />} />
            <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
            <Route path="/reviews" element={<ReviewsPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/policies/:slug" element={<PolicyPage />} />
            <Route path="/stacks" element={<StacksListPage />} />
            <Route path="/stacks/builder" element={<StackBuilderPage />} />
            <Route path="/build-a-stack" element={<StackBuilderPage />} />
            <Route path="/stacks/:slug" element={<StackDetailPage />} />
            <Route path="/lab-standards" element={<LabStandardsPage />} />
            <Route path="/order/:reference" element={<OrderInstructionsPage />} />
            <Route path="/track/:reference" element={<TrackOrderPage />} />
            <Route path="/account" element={<AccountPage />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/wishlist" element={<WishlistPage />} />
            <Route path="/coa-library" element={<CoaLibraryPage />} />
            <Route path="/official" element={<OfficialSitePage />} />
            <Route path="*" element={<NotFoundPage />} />
        </Routes>
    );
};

/**
 * AppShell — top-level renderer that picks ONE of three mounts:
 *   1. standalone gateway (no chrome)       — unauth user OR auth-only path
 *   2. storefront with full chrome          — authenticated customer / admin / bypass
 *   3. (nothing while auth is resolving)    — avoids flash of chrome
 *
 * The entire gateway can be turned OFF by an admin via Admin Panel →
 * Website Settings → Gateway Login Portal. When OFF, every path renders
 * the storefront chrome directly (except the auth-only pages themselves,
 * which stay reachable so admins can log in and flip the switch back on).
 */
const AppShell = () => {
    const { user, loading } = useAuth();
    const location = useLocation();

    // Public, uncached read of the gateway toggle. Null = not yet known.
    const [gatewayEnabled, setGatewayEnabled] = React.useState(null);
    React.useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const { data } = await api.get("/site/gateway");
                if (!cancelled) setGatewayEnabled(!!data.enabled);
            } catch {
                if (!cancelled) setGatewayEnabled(true); // fail-closed on error
            }
        };
        load();
        // Live update when the admin flips the switch in another tab.
        const t = setInterval(load, 15000);
        return () => { cancelled = true; clearInterval(t); };
    }, []);

    // Resolve admin override: admin-token holders can access /admin without a
    // customer session. The admin dashboard renders its own layout.
    const hasAdminToken =
        typeof window !== "undefined" && !!localStorage.getItem("admin_token");

    if (loading || gatewayEnabled === null) return null;

    const path = location.pathname;
    const isAuthOnly = isAuthOnlyPath(path);
    const isBypass = isBypassPath(path);
    const isAdminPath = path === "/admin" || path.startsWith("/admin/");

    // Case A — pure auth pages always render standalone (no chrome), even
    // if the visitor is somehow already authenticated. Clicking "Sign in"
    // from the storefront is covered by the logout flow + Nav /account link.
    if (isAuthOnly) {
        return <StandaloneGatewayRoutes />;
    }

    // Case B — unauth visitor on a non-bypass storefront URL. Render the
    // standalone gateway and remember the destination so sign-in bounces
    // the visitor back to it.
    //
    // Only engaged when gatewayEnabled is TRUE. When the admin toggles the
    // portal OFF the storefront is directly reachable.
    if (gatewayEnabled && !user && !isBypass) {
        const next = location.pathname + location.search;
        return (
            <Navigate
                to={`/login?next=${encodeURIComponent(next)}`}
                replace
                state={{ from: next }}
            />
        );
    }

    // Case C — admin dashboard is reachable even without a customer session
    // because admins have their own X-Admin-Token layer. Render with chrome
    // so admin tools (links back to /shop etc.) work as today.
    if (isAdminPath && !user && !hasAdminToken) {
        // Admin login form lives inside AdminPage; render chrome so an admin
        // can get to it without the customer gate interfering.
    }

    // Case D — authenticated / admin / /official / /auth/callback → full storefront chrome.
    return (
        <>
            <Header />
            <ComplianceBanner />
            <main className="min-h-screen">
                <StorefrontRoutes />
            </main>
            <Footer />
            <NewsletterPopup />
            <LiveChatWidget />
        </>
    );
};

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <AuthProvider>
                    <WishlistProvider>
                        <CartProvider>
                            <ScrollToTop />
                            <TrackerBootstrap />
                            <AnalyticsBootstrap />
                            <MaintenanceGate>
                                <AppShell />
                            </MaintenanceGate>
                            <Toaster
                                position="top-center"
                                toastOptions={{
                                    style: {
                                        background: "rgba(20, 10, 5, 0.95)",
                                        color: "#F8F4ED",
                                        border: "1px solid rgba(233, 188, 11, 0.35)",
                                        backdropFilter: "blur(20px)",
                                    },
                                }}
                            />
                        </CartProvider>
                    </WishlistProvider>
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
